import { getDays } from '@/utils/openHours';
import { ServiceAvailability, ServiceModel } from '@/typings/models/Location';
import { getIntl } from 'umi';
import dayjs from 'dayjs';

export function getLocationAllowDateTimes(service: ServiceModel) {
  if (Array.isArray(service?.calendar?.Availability)) {
    const deduplicated = deduplicateAvailability(service.calendar.Availability);
    return deduplicated.map(a => {
      if (a.allow_day_and_time) return a;
      if (a.day && a.start && a.end) return { allow_day_and_time: a };
      return null;
    }).filter(Boolean);
  }
  return [];
}
export const transformToSlotsFormat = (availability: ServiceAvailability[], intl = getIntl()) => {
  const daysLookup = getDays(intl);
  const daysOfWeek = daysLookup.map(d => d.day.toLowerCase());
  return daysOfWeek.map((day) => {
    const matchedDays = availability.filter((a) => {
      const item = a.allow_day_and_time || a;
      return item?.day?.toLowerCase() === day;
    });
    return {
      day: day.charAt(0).toUpperCase() + day.slice(1),
      title: daysLookup?.find(x => x.day === day)?.title,
      enabled: matchedDays.length > 0,
      slots: matchedDays.map((matchedDay) => {
        const item = matchedDay.allow_day_and_time || matchedDay;
        return {
          start: item.start || null,
          end: item.end || null,
        };
      }),
    };
  });
};

export const transformToServerFormat = (data, intl) => {
  const result = [];
  const daysLookup = getDays(intl);
  data.forEach((day) => {
    day?.allow_day_and_time?.slots?.forEach((slot) => {
      if (slot.start && slot.end) {
        result.push({
          allow_day_and_time: {
            day: day.allow_day_and_time?.day?.toLowerCase(),
            title: daysLookup?.find(x => x.day === day.allow_day_and_time?.day?.toLowerCase() )?.title,
            start: slot.start,
            end: slot.end,
          },
        });
      }
    });
  });

  return result;
};
export function getLocationOpenPeriods(service: ServiceModel) {
  if (Array.isArray(service?.calendar?.Availability)) {
    const deduplicated = deduplicateAvailability(service.calendar.Availability);
    return deduplicated.filter(
      (availability) =>
        availability.allow_period !== undefined ||
        (availability.start && availability.end && !availability.day && !availability.block_period),
    ).map((availability, index) => {
      const period = availability.allow_period || availability;
      return {
        ...period,
        index,
      };
    });
  }
  return [];
}

export function getLocationBlockPeriods(service: ServiceModel) {
  if (Array.isArray(service?.calendar?.Availability)) {
    const deduplicated = deduplicateAvailability(service.calendar.Availability);
    return deduplicated.filter(
      (av) => av.block_period || (av.start && av.end && !av.day && av.block_period),
    ).map((av, index) => {
      const period = typeof av.block_period === 'object' ? av.block_period : av;
      return {
        ...period,
        index,
      };
    });
  }
  return [];
}

export function deduplicateAvailability(
  availability: ServiceAvailability[],
): ServiceAvailability[] {
  const seen = new Set<string>();
  return availability.filter((item: any) => {
    let key = '';
    if (item.allow_day_and_time) {
      const { day, start, end } = item.allow_day_and_time;
      key = `allow_day_and_time:${day?.toLowerCase()}:${start}:${end}`;
    } else if (item.day && item.start && item.end) {
      // Handle legacy allow_day_and_time format
      key = `allow_day_and_time:${item.day?.toLowerCase()}:${item.start}:${item.end}`;
    } else if (item.block_period) {
      const { start, end } = typeof item.block_period === 'object' ? item.block_period : item;
      const startStr = dayjs(start).isValid() ? dayjs(start).format('YYYY-MM-DDTHH:mm:ss') : start;
      const endStr = dayjs(end).isValid() ? dayjs(end).format('YYYY-MM-DDTHH:mm:ss') : end;
      key = `block_period:${startStr}:${endStr}`;
    } else if (item.allow_period) {
      const { start, end } = item.allow_period;
      const startStr = dayjs(start).isValid() ? dayjs(start).format('YYYY-MM-DDTHH:mm:ss') : start;
      const endStr = dayjs(end).isValid() ? dayjs(end).format('YYYY-MM-DDTHH:mm:ss') : end;
      key = `allow_period:${startStr}:${endStr}`;
    } else if (item.start && item.end) {
      // Handle legacy format
      const type = item.block_period ? 'block_period' : 'allow_period';
      const startStr = dayjs(item.start).isValid() ? dayjs(item.start).format('YYYY-MM-DDTHH:mm:ss') : item.start;
      const endStr = dayjs(item.end).isValid() ? dayjs(item.end).format('YYYY-MM-DDTHH:mm:ss') : item.end;
      key = `${type}:${startStr}:${endStr}`;
    } else {
      return true;
    }

    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
}

export function getCleanServicePayload(payload: any) {
  if (payload?.calendar?.Availability) {
    return {
      ...payload,
      calendar: {
        ...payload.calendar,
        Availability: deduplicateAvailability(payload.calendar.Availability),
      },
    };
  }
  return payload;
}

/**
 * Which branches have to be refetched after an agent↔service assignment changed.
 * Callers know the service ids for sure but not always the branch (the user drawer
 * only holds services), so unknown branches are resolved from the branches already
 * in the store — a service whose branch isn't loaded has nothing on screen to refresh.
 */
export function resolveBranchIdsForServices(
  locationState: any,
  { branchIds = [], serviceIds = [] }: { branchIds?: (string | undefined)[]; serviceIds?: (string | undefined)[] },
) {
  const knownBranches = [
    ...Object.values(locationState?.branchDetails || {}),
    ...(locationState?.fullBranchesList || []),
  ] as any[];

  const resolved = new Set<string>(branchIds.filter(Boolean).map((id) => `${id}`));

  serviceIds.filter(Boolean).forEach((serviceId) => {
    const branch = knownBranches.find((b) =>
      (b?.services || b?.branch_services || []).some((service: any) => `${service?.id}` === `${serviceId}`),
    );
    if (branch?.id) resolved.add(`${branch.id}`);
  });

  return [...resolved];
}

export function extractUsersFromBranches(branches: any) {
  const users = [];
  const branchArray = Array.isArray(branches) ? branches : [branches];
  branchArray.forEach((branch) => {
    if (!branch) return;
    if (branch.staff && Array.isArray(branch.staff)) users.push(...branch.staff);
    if (branch.services && Array.isArray(branch.services)) {
      branch.services.forEach((service) => {
        if (service.agents && Array.isArray(service.agents)) users.push(...service.agents);
        if (service.managers && Array.isArray(service.managers)) users.push(...service.managers);
      });
    }
  });
  return users;
}

export const ZIP_CODE_MAX_LENGTH = 32;

/**
 * Splits typed or pasted input into zip codes and appends the new ones.
 * Spaces are kept inside a code (NL "1012 AB", UK "SW1A 1AA"); only , ; and line breaks separate.
 * Duplicates are case-insensitive: the column collation is _ci, so "1012 ab" next to "1012 AB" hits the unique index.
 */
export function addZipCodes(existing: string[], input: string) {
  const zipCodes = [...existing];
  const seen = new Set(existing.map((zipCode) => zipCode.toLowerCase()));
  const duplicates: string[] = [];
  const tooLong: string[] = [];

  input.split(/[,;\r\n\t]+/).forEach((token) => {
    const zipCode = token.trim();
    if (!zipCode) return;
    const key = zipCode.toLowerCase();
    if (zipCode.length > ZIP_CODE_MAX_LENGTH) {
      tooLong.push(zipCode);
    } else if (seen.has(key)) {
      if (!duplicates.includes(zipCode)) duplicates.push(zipCode);
    } else {
      seen.add(key);
      zipCodes.push(zipCode);
    }
  });

  return { zipCodes, duplicates, tooLong };
}
